import React, { useState, useEffect } from "react";
import { Header } from "../components/Header";
import axios from "axios";
import { API_ENDPOINTS } from '../config/api';
import { MessageSquare, ChartBar, MousePointerClick, ExternalLink } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../config/amplitude';

interface TopPost {
  postId: string;
  subreddit: string;
  clicks: number;
  lastClick: string;
}

export const AnalyticsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState<{type: 'success' | 'error' | 'info', text: string} | null>(null);
  const [commentStats, setCommentStats] = useState({
    totalComments: 0,
    commentedPosts: 0,
    subreddits: 0
  });
  const [clickStats, setClickStats] = useState({
    totalClicks: 0,
    uniqueUsers: 0
  });
  const [topPosts, setTopPosts] = useState<TopPost[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('reddit_token');
        if (!token) {
          handleLogout();
          return;
        }

        // Fetch click statistics
        const clicksResponse = await axios.get(`${API_ENDPOINTS.CLICKS}/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setClickStats({
          totalClicks: clicksResponse.data.totalClicks || 0,
          uniqueUsers: new Set(clicksResponse.data.details.map((click: any) => click.userId)).size
        });
        
        setTopPosts(clicksResponse.data.topPosts || []);

        // Fetch comment statistics
        const commentsResponse = await axios.get(API_ENDPOINTS.USER_COMMENTS, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setCommentStats({
          totalComments: commentsResponse.data.totalComments,
          commentedPosts: commentsResponse.data.uniquePosts,
          subreddits: commentsResponse.data.uniqueSubreddits
        });

      } catch (err: any) {
        console.error('Error fetching analytics:', err);
        if (err.response?.status === 401) {
          handleLogout();
        } else {
          setStatusMessage({type: 'error', text: 'Failed to load analytics'});
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('reddit_token');
    localStorage.removeItem('reddit_user');
    setStatusMessage({type: 'info', text: 'Session expired. Please log in again.'});
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gradientStart to-gradientEnd">
      <Header 
        isAuthenticated={true}
        onLogout={handleLogout}
        onLogin={() => navigate('/')}
      />
      
      {/* Status Message */}
      {statusMessage && (
        <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-md shadow-md z-50 ${
          statusMessage.type === 'success' ? 'bg-green-100 text-green-700' :
          statusMessage.type === 'error' ? 'bg-red-100 text-red-700' :
          'bg-blue-100 text-blue-700'
        } transition-opacity duration-300`}>
          {statusMessage.text}
        </div>
      )}

      <div className="max-w-4xl mx-auto py-8 px-4 mt-20">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-6">
            <ChartBar className="text-sky-500 mr-3" size={24} />
            <h1 className="text-2xl font-bold text-gray-800">Analytics Dashboard</h1>
          </div>
        
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            {/* Comments Stats */}
            <div className="bg-gradient-to-br from-sky-500 to-sky-600 p-4 rounded-lg shadow-lg text-white">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Comments</h2>
              </div>
              <div className="space-y-1">
                <p className="text-2xl font-bold">{commentStats.totalComments}</p>
                <p className="text-sm opacity-90">Total Comments</p>
              </div>
            </div>

            {/* Posts Stats */}
            <div className="bg-gradient-to-br from-sky-500 to-sky-600 p-4 rounded-lg shadow-lg text-white">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Posts</h2>
              </div>
              <div className="space-y-1">
                <p className="text-2xl font-bold">{commentStats.commentedPosts}</p>
                <p className="text-sm opacity-90">Posts Commented</p>
              </div>
            </div>

            {/* Subreddits Stats */}
            <div className="bg-gradient-to-br from-sky-500 to-sky-600 p-4 rounded-lg shadow-lg text-white">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Subreddits</h2>
              </div>
              <div className="space-y-1">
                <p className="text-2xl font-bold">{commentStats.subreddits}</p>
                <p className="text-sm opacity-90">Active Subreddits</p>
              </div>
            </div>

            {/* Click Stats */}
            <div className="bg-gradient-to-br from-sky-500 to-sky-600 p-4 rounded-lg shadow-lg text-white">
              <div className="flex items-center gap-2 mb-2">
                <MousePointerClick className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Clicks</h2>
              </div>
              <div className="space-y-1">
                <p className="text-2xl font-bold">{clickStats.totalClicks}</p>
                <p className="text-sm opacity-90">Total Clicks</p>
              </div>
            </div>
          </div>

          {/* Top Posts Table */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-4">
              <ChartBar className="w-5 h-5 text-sky-500" />
              <h2 className="text-lg font-semibold text-gray-800">Top Posts by Clicks</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 px-4 text-gray-600">Post</th>
                    <th className="py-2 px-4 text-gray-600">Subreddit</th>
                    <th className="py-2 px-4 text-gray-600">Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {topPosts.map((post) => (
                    <tr key={post.postId} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4">
                        <a
                          href={`https://www.reddit.com/r/${post.subreddit}/comments/${post.postId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sky-500 hover:text-sky-600 font-mono text-sm inline-flex items-center gap-1"
                          onClick={() => {
                            trackEvent('Post Id Clicked', {
                              postId: post.postId,
                              subreddit: post.subreddit,
                              clicks: post.clicks,
                              lastClick: post.lastClick
                            });
                          }}
                        >
                          {post.postId} <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td className="py-2 px-4">r/{post.subreddit}</td>
                      <td className="py-2 px-4">{post.clicks}</td>
                    </tr>
                  ))}
                  {topPosts.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-4 text-center text-gray-500">
                        No click data available yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 